<template>
  <div>
    <h1>{{ $t("profile.title") }}</h1>

    <div class="mt-3 elevation-12 grey lighten-4">
      <v-tabs centered icons-and-text>
        <v-tab key="general">
          {{ $t("profile.personalData") }}
          <v-icon>account_box</v-icon>
        </v-tab>
        <v-tab key="permissions">
          {{ $t("profile.rolesAndPermissions") }}
          <v-icon>pan_tool</v-icon>
        </v-tab>
        <v-tab-item key="general">
          <v-card>
            <v-card-text>
              <v-form ref="form" v-model="formValid" lazy-validation>
                <v-radio-group v-model="users.sex" :mandatory="false" row disabled>
                  <v-radio :label="$t('global.mr')" :value="1" />
                  <v-radio :label="$t('global.ms')" :value="2" />
                  <v-radio :label="$t('global.notSet')" :value="0" />
                </v-radio-group>
                <v-text-field :label="$t('profile.fields.name.name') + '*'" v-model="users.name" disabled :rules="users.nameRules" />
                <v-text-field
                  :label="$t('settings.users.basicData.fields.middleName.name')"
                  v-if="!$store.getters['isAdmin']"
                  v-model="users.middle_name"
                  :placeholder="$t('global.optional')"
                />
                <v-text-field :label="$t('profile.fields.surname.name') + '*'" v-model="users.surname" disabled :rules="users.surnameRules" />
                <v-text-field :label="$t('profile.fields.login.name') + '*'" v-model="users.login" disabled :rules="users.loginRules" />
                <v-text-field :label="$t('profile.fields.email.name') + '*'" v-model="users.email" disabled :rules="users.emailRules" />
                <v-text-field :label="$t('profile.fields.integrationIdentifier.name')" disabled v-model="users.integration_identifier" />
                <v-text-field :label="$t('profile.fields.superiorUser.name')" v-model="users.superior_user_name" disabled />
                <v-text-field
                  :label="$t('profile.fields.organizationStructure.name')"
                  v-model="users.organization_structure_name"
                  disabled
                  v-if="$store.getters['userProperties'].system_settings.is_module_delegation_enabled === 2"
                />
                <v-text-field
                  :label="$t('profile.fields.password.name')"
                  password
                  v-model="users.password"
                  type="password"
                  :rules="!users.password ? [] : users.passwordRules"
                  :placeholder="users.id != null ? $t('profile.fields.password.noChange') : ''"
                />
                <v-text-field
                  :label="$t('profile.fields.repeatPassword.name')"
                  type="password"
                  password
                  v-model="users.repeatPassword"
                  :rules="users.repeatPasswordRules"
                  :placeholder="users.id != null ? $t('profile.fields.repeatPassword.noChange') : ''"
                />
                <v-autocomplete
                  :label="$t('profile.fields.privateVehicle.name')"
                  v-model="users.vehicle_private_id"
                  :items="availablePrivateVehicles"
                  v-if="$store.getters['userProperties'].system_settings.is_module_delegation_enabled === 2"
                />
                <v-autocomplete
                  :label="$t('profile.fields.defaultVehicle.name')"
                  v-model="users.vehicle_default_id"
                  :items="availableDefaultVehicles"
                  v-if="$store.getters['userProperties'].system_settings.is_module_delegation_enabled === 2"
                />
                <v-text-field
                  :label="$t('profile.fields.bankName.name')"
                  v-model="users.bank_name"
                  v-if="$store.getters['userProperties'].system_settings.is_module_delegation_enabled === 2"
                />
                <v-text-field
                  :label="$t('profile.fields.bankAccount.name')"
                  v-model="users.bank_account_number"
                  v-if="$store.getters['userProperties'].system_settings.is_module_delegation_enabled === 2"
                />
                <location-ajax-select
                  :select-model.sync="users.home_address_id"
                  :placeholder="users.home_address_name"
                  :label="$t('global.homeLocation')"
                  v-if="$store.getters['userProperties'].system_settings.is_module_delegation_enabled === 2"
                />
                <location-ajax-select
                  :select-model.sync="users.work_address_id"
                  disabled
                  :placeholder="users.work_address_name"
                  :label="$t('global.workLocation')"
                  v-if="$store.getters['userProperties'].system_settings.is_module_delegation_enabled === 2"
                />
                <v-text-field
                  :label="$t('profile.fields.delegationRateGroup.name')"
                  v-model="users.group_name"
                  disabled
                  v-if="$store.getters['userProperties'].system_settings.is_module_delegation_enabled === 2"
                />
                <v-text-field
                  :label="$t('settings.users.basicData.fields.phone.name')"
                  placeholder="+48 111-222-333"
                  :rules="users.phoneNumberRules"
                  class="user-mobile-number"
                  mask="+## ###-###-###"
                  required
                  v-model="users.phone"
                  v-if="
                    $store.getters['userProperties'].system_settings.is_module_travel_service_enabled === 2 ||
                      $store.getters['userProperties'].system_settings.is_module_delegation_enabled === 2
                  "
                />
                <date-picker
                  :model.sync="users.birth_date"
                  :label="$t('settings.users.basicData.fields.birthDate.name')"
                  v-if="$store.getters['userProperties'].system_settings.is_module_travel_service_enabled === 2"
                />
                <v-text-field
                  :label="$t('settings.users.basicData.fields.documentNumber.name')"
                  placeholder="ABC 123456"
                  v-model="users.document_number"
                  v-if="$store.getters['userProperties'].system_settings.is_module_travel_service_enabled === 2"
                />
                <date-picker
                  :model.sync="users.document_expire"
                  :label="$t('settings.users.basicData.fields.documentExpire.name')"
                  v-if="!$store.getters['isAdmin'] && $store.getters['userProperties'].system_settings.is_module_travel_service_enabled === 2"
                  placeholder="2020-01-01"
                  :clearable="true"
                />
                <v-text-field
                  :label="$t('settings.users.basicData.fields.documentIssuePlace.name')"
                  placeholder="Warszawa"
                  v-if="!$store.getters['isAdmin'] && $store.getters['userProperties'].system_settings.is_module_travel_service_enabled === 2"
                  v-model="users.document_issue_place"
                />
                <v-text-field
                  :label="$t('settings.users.basicData.fields.nationality.name')"
                  :placeholder="$t('settings.users.basicData.fields.nationality.placeholder')"
                  v-if="!$store.getters['isAdmin'] && $store.getters['userProperties'].system_settings.is_module_travel_service_enabled === 2"
                  v-model="users.nationality"
                />
                <v-checkbox
                  :label="$t('settings.users.basicData.fields.isNotifyNewTravelServiceResponse.name')"
                  v-if="$store.getters.hasPermission('travelOrder') && $store.getters['userProperties'].system_settings.is_module_travel_service_enabled === 2"
                  v-model="users.is_notify_new_travel_service_response"
                />
              </v-form>
            </v-card-text>
          </v-card>
        </v-tab-item>
        <v-tab-item key="permissions">
          <v-card>
            <v-card-text>
              <h2>{{ $t("profile.hasPermissions") }}</h2>

              <v-data-table
                :headers="headers"
                :items="hasAuthItems"
                hide-default-footer
                :footer-props="{ 'items-per-page-options': [{ text: '$vuetify.dataIterator.rowsPerPageAll', value: -1 }] }"
              >
                <template #item="props">
                  <tr>
                    <td class="text-left">
                      {{ props.item.type == 2 ? $t("global.permission") : $t("global.role") }}
                    </td>
                    <td class="text-left">{{ props.item.translated_name }}</td>
                    <td class="text-left">{{ props.item.description }}</td>
                  </tr>
                </template>
                <template slot="no-data">
                  <v-alert color="error" icon="warning">
                    {{ $t("profile.noPermission") }}
                  </v-alert>
                </template>
              </v-data-table>
            </v-card-text>
          </v-card>
        </v-tab-item>
      </v-tabs>

      <v-card>
        <v-card-actions>
          <v-spacer />
          <v-btn color="blue darken-1" text @click.native="cancel()">{{ $t("global.cancel") }}</v-btn>
          <v-btn color="blue darken-1" text :disabled="!formValid" @click.native="save()">{{ $t("global.save") }}</v-btn>
        </v-card-actions>
      </v-card>
    </div>
  </div>
</template>

<script>
import user from "../../models/user";
import vehicleTypes from "../../staticData/VehicleTypes";
import LocationAjaxSelect from "../../components/CustomInputComponents/LocationAjaxSelect.vue";
import DatePicker from "../../components/CustomInputComponents/DatePicker";

export default {
  name: "MyProfileView",
  components: { DatePicker, LocationAjaxSelect },
  data: () => ({
    users: null,
    allPrivateVehicles: [],
    formValid: true,
    allVehicles: [],
    allAuthItems: []
  }),
  created() {
    this.users = new user(this.$store.getters["userProperties"].user_id, "settings/my-profile/load");

    this.axios.get("settings/my-profile/available-private-vehicles").then(resp => {
      this.allPrivateVehicles = resp.data;
    });
    this.axios.get("settings/my-profile/available-default-vehicles").then(resp => {
      this.allVehicles = resp.data;
    });
    this.axios.get("settings/my-profile/available-auth-items").then(resp => {
      this.allAuthItems = resp.data;
    });
  },
  computed: {
    headers() {
      return [
        { text: this.$t("profile.typ"), value: "type", width: "10%" },
        { text: this.$t("profile.name"), value: "name", width: "10%" },
        {
          text: this.$t("profile.description"),
          value: "description",
          width: "80%"
        }
      ];
    },
    vehicleTypes: vehicleTypes,
    availablePrivateVehicles() {
      const array = [];
      for (let vehicle of this.allPrivateVehicles) {
        array.push({
          value: vehicle.id,
          text:
            this.vehicleTypes.filter(vehicleType => vehicleType.id === vehicle.type).pop().name +
            " " +
            vehicle.model +
            " " +
            vehicle.engine_capacity +
            " cm3 (" +
            vehicle.plates +
            ")"
        });
      }
      return array;
    },
    availableDefaultVehicles() {
      const array = [];
      for (let vehicle of this.allVehicles) {
        array.push({
          value: vehicle.id,
          text:
            this.vehicleTypes.filter(vehicleType => vehicleType.id === vehicle.type).pop().name +
            " " +
            vehicle.model +
            " " +
            vehicle.engine_capacity +
            " cm3 (" +
            vehicle.plates +
            ")"
        });
      }
      return array;
    },

    hasAuthItems() {
      const array = [];
      for (let authItem of this.allAuthItems) {
        for (let userAuthItem of this.users.auth_items) {
          if (userAuthItem === authItem.name) {
            array.push(authItem);
          }
        }
      }
      return array;
    }
  },
  methods: {
    save() {
      if (this.$refs.form.validate()) {
        this.users
          .save("settings/my-profile/save")
          .then(() => {
            this.$store.dispatch("snackbarPush", {
              text: this.$t("global.saved"),
              position: "down"
            });
            //this.$router.push({path: '/users'});
          })
          .catch(() => {
            this.$store.dispatch("alertDialog", {
              content: this.$t("global.error.generalError.content"),
              title: this.$t("global.error.generalError.title")
            });
          });
      }
    },
    addAuthItem(authItemName) {
      this.users.auth_items.push(authItemName);
    },
    removeAuthItem(authItemName) {
      this.users.auth_items.splice(this.users.auth_items.indexOf(authItemName), 1);
    },
    filterAlreadyHaveItems(item) {
      for (let userAuthItem of this.users.auth_items) {
        if (userAuthItem === item.name) {
          return false;
        }
      }
      return true;
    }
  }
};
</script>

<style scoped></style>
